import { Palette, Puzzle, Crown, Frame, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

interface CollectionSummaryProps {
  ownedByCategory: Record<string, number>;
  totalByCategory: Record<string, number>;
}

const categoryConfig = {
  board_theme: { icon: Palette, label: 'Board Themes', color: 'text-purple-500' },
  piece_style: { icon: Puzzle, label: 'Piece Styles', color: 'text-blue-500' },
  avatar_accessory: { icon: Crown, label: 'Accessories', color: 'text-yellow-500' },
  profile_decoration: { icon: Frame, label: 'Frames', color: 'text-pink-500' },
};

export function CollectionSummary({ ownedByCategory, totalByCategory }: CollectionSummaryProps) {
  const totalOwned = Object.values(ownedByCategory).reduce((a, b) => a + b, 0);
  const totalItems = Object.values(totalByCategory).reduce((a, b) => a + b, 0);
  const overallProgress = totalItems > 0 ? (totalOwned / totalItems) * 100 : 0;

  return (
    <Card className="border-primary/20 bg-gradient-to-r from-primary/5 via-card to-accent/5">
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 rounded-full bg-primary/10">
            <CheckCircle2 className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="font-semibold">Your Collection</p>
            <p className="text-xs text-muted-foreground">
              {totalOwned} of {totalItems} items collected
            </p>
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold text-primary">{Math.round(overallProgress)}%</span>
          </div>
        </div>
        
        <Progress value={overallProgress} className="h-2 mb-4" />
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(categoryConfig).map(([category, config]) => {
            const owned = ownedByCategory[category] || 0;
            const total = totalByCategory[category] || 0;
            const Icon = config.icon;
            
            return (
              <div 
                key={category}
                className="flex items-center gap-2 p-2 rounded-lg bg-muted/50"
              >
                <Icon className={`h-4 w-4 ${config.color}`} />
                <div className="text-sm">
                  <span className="font-medium">{owned}</span>
                  <span className="text-muted-foreground">/{total}</span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
